/**
 * Helpers around the target-system workflow graph:
 *
 *  - an editable in-memory model (states dictionary + flat edge list) that the
 *    canvas and inspectors operate on;
 *  - conversion from the compiled graph (what GET returns) into that model and
 *    from the model into the DSL document that POST accepts;
 *  - local validation that catches what the write endpoint would reject anyway;
 *  - error extraction for the 401/403/409/422/502 responses described in
 *    docs/WORKFLOW_API.md.
 */
import dagre from 'dagre';
import type {
  WorkflowCardRef,
  WorkflowDsl,
  WorkflowDslTransition,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowGuard,
  WorkflowInterrupt,
  WorkflowPrompts,
  WorkflowState,
} from '../types/workflow';

// ─── Editable model ─────────────────────────────────────────────────────────

export interface EditableEdge extends WorkflowEdge {
  /** Local id for selection — server edges have no identity of their own. */
  id: string;
  /**
   * Card version pinned explicitly by the user. `undefined` means "latest at the
   * time the graph is applied" and the version is not sent at all.
   */
  refPin?: number;
}

export interface EditableWorkflow {
  name: string;
  entry: string;
  states: Record<string, WorkflowState>;
  edges: EditableEdge[];
  interrupts: WorkflowInterrupt[];
  metadata: Record<string, unknown> | null;
}

let edgeUid = 0;
export function nextEdgeId(source: string, target: string): string {
  edgeUid += 1;
  return `edge:${source}->${target}#${edgeUid}`;
}

let stateUid = 0;
export function nextStateId(existing: Record<string, unknown>): string {
  let id = '';
  do {
    stateUid += 1;
    id = `new_state_${stateUid}`;
  } while (existing[id]);
  return id;
}

export const REQUIRED_PROMPT_KEYS: Array<keyof WorkflowPrompts> = [
  'system_prompt',
  'reminder_prompt',
  'stage_context_prompt',
];

export const OPTIONAL_PROMPT_KEYS: Array<keyof WorkflowPrompts> = [
  'situational_prompt',
  'corrective_prompt',
  'alliance_check_prompt',
  'unit_type_check_prompt',
];

export function blankState(stateId: string): WorkflowState {
  return {
    state_id: stateId,
    state_name: stateId,
    primary_goal: '',
    secondary_goals: [],
    prompts: { system_prompt: '', reminder_prompt: '', stage_context_prompt: '' },
  };
}

export function interruptsToList(
  value: WorkflowGraph['interrupts'] | undefined
): WorkflowInterrupt[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((it) => ({ ...it }));
  return Object.keys(value).map((key) => {
    const it = value[key];
    return { ...it, name: it?.name || key };
  });
}

/** Compiled graph (GET) → editable model. Every unknown field is kept as-is. */
export function graphToEditable(graph: WorkflowGraph): EditableWorkflow {
  const states: Record<string, WorkflowState> = {};
  Object.keys(graph.states || {}).forEach((id) => {
    const s = graph.states[id];
    states[id] = { ...s, state_id: s?.state_id || id };
  });

  const edges: EditableEdge[] = [];
  Object.keys(graph.adjacency || {}).forEach((sourceId) => {
    (graph.adjacency[sourceId] || []).forEach((e) => {
      const source = e.source || sourceId;
      edges.push({
        ...e,
        source,
        target: e.target,
        type: e.type || 'conditional',
        priority: typeof e.priority === 'number' ? e.priority : 0,
        guard: e.guard || {},
        id: nextEdgeId(source, e.target),
      });
    });
  });

  return {
    name: graph.name || 'workflow',
    entry: graph.entry || '',
    states,
    edges,
    interrupts: interruptsToList(graph.interrupts),
    metadata: graph.metadata ?? null,
  };
}

// ─── Guards ─────────────────────────────────────────────────────────────────

export type GuardKind = 'always' | 'card' | 'trigger' | 'flags_all' | 'flags_any' | 'expression' | 'empty';

/**
 * Reference to a library card as seen on a compiled edge. The server expands
 * `on_exit_trigger_ref` into a plain trigger and hides the pin in
 * `on_exit_trigger.ref`, so a card edge is recognised by `ref`, not by the DSL field.
 */
export function cardRefOf(guard: WorkflowGuard | undefined | null): WorkflowCardRef | null {
  return guard?.on_exit_trigger?.ref ?? guard?.on_exit_trigger_ref ?? null;
}

export function guardKind(guard: WorkflowGuard | undefined | null): GuardKind {
  if (!guard) return 'empty';
  if (guard.always) return 'always';
  if (cardRefOf(guard)) return 'card';
  if (guard.on_exit_trigger) return 'trigger';
  if (guard.flags_all && guard.flags_all.length) return 'flags_all';
  if (guard.flags_any && guard.flags_any.length) return 'flags_any';
  if (guard.expression) return 'expression';
  return 'empty';
}

export function guardLabel(guard: WorkflowGuard | undefined | null): string {
  switch (guardKind(guard)) {
    case 'always':
      return 'always';
    case 'card': {
      const ref = cardRefOf(guard)!;
      return `[card] ${ref.name}${ref.version ? ` v${ref.version}` : ''}`;
    }
    case 'trigger': {
      const t = guard!.on_exit_trigger!;
      return `${t.name} ≥ ${t.min_confidence}`;
    }
    case 'flags_all':
      return `flags_all: ${guard!.flags_all!.join(', ')}`;
    case 'flags_any':
      return `flags_any: ${guard!.flags_any!.join(', ')}`;
    case 'expression':
      return `expr: ${guard!.expression}`;
    default:
      return '';
  }
}

// ─── Editable → DSL ─────────────────────────────────────────────────────────

/**
 * Builds the DSL document for `changes.workflow`. Mirrors what the target
 * system's own editor sends:
 *
 *  - `exit_triggers` is computed by the backend from outgoing edges and is
 *    rejected on write — it is stripped;
 *  - a state with `strategies_ref` arrives expanded (`strategies` filled next to
 *    the ref). Sending both is rejected, sending only the inline block drops the
 *    reference, so the ref wins;
 *  - a card edge arrives expanded with the pin in `on_exit_trigger.ref`; it is
 *    folded back into `on_exit_trigger_ref`, and the version is sent only when
 *    the user pinned it explicitly.
 */
export function editableToDsl(wf: EditableWorkflow): WorkflowDsl {
  const states = Object.keys(wf.states).map((id) => {
    const src = wf.states[id];
    const state: Record<string, unknown> = { ...src, state_id: src.state_id || id };
    delete state.exit_triggers;
    if (state.strategies_ref != null) delete state.strategies;
    return state as unknown as Omit<WorkflowState, 'exit_triggers'>;
  });

  const transitions: WorkflowDslTransition[] = wf.edges.map((e) => {
    const ref = cardRefOf(e.guard);
    const guard: WorkflowGuard = ref
      ? { on_exit_trigger_ref: { name: ref.name, ...(e.refPin != null ? { version: e.refPin } : {}) } }
      : normalizeInlineGuard(e.guard);
    const t: WorkflowDslTransition = {
      from: e.source,
      to: e.target,
      type: e.type || 'conditional',
      guard,
      priority: typeof e.priority === 'number' ? e.priority : 0,
    };
    if (e.reason != null && e.reason !== '') t.reason = e.reason;
    if (e.applied_flags && e.applied_flags.length) t.applied_flags = e.applied_flags;
    if (e.metadata) t.metadata = e.metadata;
    return t;
  });

  const dsl: WorkflowDsl = {
    name: wf.name || 'workflow',
    entry: wf.entry,
    states,
    transitions,
  };
  if (wf.interrupts.length) dsl.interrupts = wf.interrupts.map(cleanInterrupt);
  if (wf.metadata && Object.keys(wf.metadata).length) dsl.metadata = wf.metadata;
  return dsl;
}

/** Keeps exactly one guard variant and drops the empty leftovers of the others. */
function normalizeInlineGuard(guard: WorkflowGuard | undefined | null): WorkflowGuard {
  const kind = guardKind(guard);
  switch (kind) {
    case 'always':
      return { always: true };
    case 'trigger': {
      const t = guard!.on_exit_trigger!;
      const out: WorkflowGuard = {
        on_exit_trigger: {
          name: t.name,
          min_confidence: typeof t.min_confidence === 'number' ? t.min_confidence : 0.7,
          ...(t.description ? { description: t.description } : {}),
        },
      };
      return out;
    }
    case 'flags_all':
      return { flags_all: guard!.flags_all! };
    case 'flags_any':
      return { flags_any: guard!.flags_any! };
    case 'expression':
      return { expression: guard!.expression! };
    default:
      return { always: true };
  }
}

/** The server model is `extra="forbid"` — send only the known keys, and no empty description. */
function cleanInterrupt(it: WorkflowInterrupt): WorkflowInterrupt {
  const out: WorkflowInterrupt = { name: (it.name || '').trim(), target_state_id: it.target_state_id };
  if (it.description != null && String(it.description).trim() !== '') out.description = it.description;
  return out;
}

// ─── Import (DSL or compiled workflow document from a file) ────────────────

export function anyGraphToEditable(input: unknown): EditableWorkflow {
  if (!input || typeof input !== 'object') throw new Error('The workflow file must contain a structured workflow document.');
  const doc = input as Record<string, unknown>;
  // A full GET snapshot or a `workflow` document — unwrap to the graph.
  const graphLike = (doc.workflow && typeof doc.workflow === 'object' && (doc.workflow as any).graph)
    ? (doc.workflow as any).graph
    : doc.graph && typeof doc.graph === 'object'
      ? doc.graph
      : doc;
  const g = graphLike as Record<string, unknown>;

  if (Array.isArray(g.states)) {
    // DSL form
    const states: Record<string, WorkflowState> = {};
    (g.states as WorkflowState[]).forEach((s, i) => {
      if (!s || typeof s !== 'object' || !s.state_id) throw new Error(`states[${i}] has no state_id.`);
      states[s.state_id] = { ...s };
    });
    const transitions = Array.isArray(g.transitions) ? (g.transitions as WorkflowDslTransition[]) : [];
    const edges: EditableEdge[] = transitions.map((t, i) => {
      const from = (t as any).from ?? (t as any).source;
      const to = (t as any).to ?? (t as any).target;
      if (!from || !to) throw new Error(`transitions[${i}] must have from/to.`);
      const rest: Record<string, unknown> = { ...t };
      delete rest.from;
      delete rest.to;
      return {
        ...(rest as Partial<WorkflowEdge>),
        source: from,
        target: to,
        type: t.type || 'conditional',
        priority: typeof t.priority === 'number' ? t.priority : 0,
        guard: t.guard || { always: true },
        id: nextEdgeId(from, to),
      };
    });
    return {
      name: typeof g.name === 'string' ? g.name : 'workflow',
      entry: typeof g.entry === 'string' ? g.entry : '',
      states,
      edges,
      interrupts: interruptsToList(g.interrupts as WorkflowGraph['interrupts']),
      metadata: (g.metadata as Record<string, unknown>) ?? null,
    };
  }

  if (g.states && typeof g.states === 'object') {
    return graphToEditable({
      name: typeof g.name === 'string' ? g.name : 'workflow',
      entry: typeof g.entry === 'string' ? g.entry : '',
      states: g.states as Record<string, WorkflowState>,
      adjacency: (g.adjacency as Record<string, WorkflowEdge[]>) || {},
      interrupts: g.interrupts as WorkflowGraph['interrupts'],
      metadata: (g.metadata as Record<string, unknown>) ?? null,
    });
  }

  throw new Error('Unrecognised workflow file: expected a workflow with states and transitions.');
}

// ─── Mutations that must cascade ────────────────────────────────────────────

export function renameState(wf: EditableWorkflow, oldId: string, newId: string): EditableWorkflow {
  if (!newId || oldId === newId || wf.states[newId]) return wf;
  const states: Record<string, WorkflowState> = {};
  Object.keys(wf.states).forEach((id) => {
    if (id === oldId) states[newId] = { ...wf.states[id], state_id: newId };
    else states[id] = wf.states[id];
  });
  return {
    ...wf,
    entry: wf.entry === oldId ? newId : wf.entry,
    states,
    edges: wf.edges.map((e) => ({
      ...e,
      source: e.source === oldId ? newId : e.source,
      target: e.target === oldId ? newId : e.target,
    })),
    interrupts: wf.interrupts.map((it) =>
      it.target_state_id === oldId ? { ...it, target_state_id: newId } : it
    ),
  };
}

export function removeState(wf: EditableWorkflow, id: string): EditableWorkflow {
  const states = { ...wf.states };
  delete states[id];
  const remainingIds = Object.keys(states);
  return {
    ...wf,
    entry: wf.entry === id ? remainingIds[0] || '' : wf.entry,
    states,
    edges: wf.edges.filter((e) => e.source !== id && e.target !== id),
    // An interrupt pointing at a removed state is rejected on save.
    interrupts: wf.interrupts.filter((it) => it.target_state_id !== id),
  };
}

// ─── Validation ─────────────────────────────────────────────────────────────

export function validateEditable(wf: EditableWorkflow): string[] {
  const problems: string[] = [];
  const ids = Object.keys(wf.states);
  if (!ids.length) problems.push('The graph has no states.');
  if (!wf.entry) problems.push('Entry state is not set — select a state and mark it as entry.');
  else if (!wf.states[wf.entry]) problems.push(`Entry state "${wf.entry}" is not on the canvas.`);

  ids.forEach((id) => {
    const s = wf.states[id];
    if (!s.state_id) problems.push(`State "${id}" has an empty state_id.`);
    if (!s.state_name || !String(s.state_name).trim()) problems.push(`State "${id}": state_name is empty.`);
    if (!s.prompts || typeof s.prompts !== 'object') {
      problems.push(`State "${id}": prompts object is missing.`);
    } else {
      REQUIRED_PROMPT_KEYS.forEach((key) => {
        if (typeof s.prompts[key] !== 'string') problems.push(`State "${id}": prompts.${String(key)} must be a string.`);
      });
    }
    if (!Array.isArray(s.secondary_goals)) problems.push(`State "${id}": secondary_goals must be a list.`);
    if (s.strategies_ref != null && !s.strategies_ref.name) {
      problems.push(`State "${id}": strategies_ref has no name.`);
    }
  });

  wf.edges.forEach((e) => {
    const label = `${e.source} → ${e.target}`;
    if (!wf.states[e.source]) problems.push(`Transition ${label}: source state does not exist.`);
    if (!wf.states[e.target]) problems.push(`Transition ${label}: target state does not exist.`);
    if (e.source === e.target) problems.push(`Transition ${label}: a state cannot transition to itself.`);
    const kind = guardKind(e.guard);
    if (kind === 'trigger' && !e.guard.on_exit_trigger?.name?.trim()) {
      problems.push(`Transition ${label}: exit trigger has no name.`);
    }
    if (kind === 'card' && !cardRefOf(e.guard)?.name) {
      problems.push(`Transition ${label}: trigger card reference has no name.`);
    }
    if (kind === 'expression' && !e.guard.expression?.trim()) {
      problems.push(`Transition ${label}: guard expression is empty.`);
    }
  });

  const seen: Record<string, boolean> = {};
  wf.interrupts.forEach((it, i) => {
    const name = (it.name || '').trim();
    const label = name || `#${i + 1}`;
    if (!name) problems.push(`Interrupt #${i + 1}: name is empty.`);
    else if (seen[name]) problems.push(`Interrupt "${label}": duplicate name.`);
    seen[name] = true;
    if (!it.target_state_id) problems.push(`Interrupt "${label}": target state is not selected.`);
    else if (!wf.states[it.target_state_id]) {
      problems.push(`Interrupt "${label}": target "${it.target_state_id}" is not on the canvas.`);
    }
  });

  return problems;
}

// ─── Derived data for the UI ────────────────────────────────────────────────

/** Every flag name mentioned anywhere in the graph — suggestions for chip inputs. */
export function collectFlags(wf: EditableWorkflow): string[] {
  const set: Record<string, true> = {};
  const add = (list?: string[] | null) => (list || []).forEach((f) => { if (f) set[f] = true; });
  Object.keys(wf.states).forEach((id) => {
    add(wf.states[id].coordinator_flags_required);
    add(wf.states[id].coordinator_flags_set);
  });
  wf.edges.forEach((e) => {
    add(e.applied_flags);
    add(e.guard?.flags_all);
    add(e.guard?.flags_any);
  });
  return Object.keys(set).sort();
}

/** Exit triggers as the backend would compute them: from outgoing non-manual edges. */
export function computedExitTriggers(wf: EditableWorkflow, stateId: string): Array<{ name: string; min_confidence: number }> {
  const byName: Record<string, number> = {};
  wf.edges
    .filter((e) => e.source === stateId && e.type !== 'manual' && e.guard?.on_exit_trigger)
    .forEach((e) => {
      const t = e.guard.on_exit_trigger!;
      const prev = byName[t.name];
      byName[t.name] = prev == null ? t.min_confidence : Math.min(prev, t.min_confidence);
    });
  return Object.keys(byName).map((name) => ({ name, min_confidence: byName[name] }));
}

export interface LayoutPosition {
  x: number;
  y: number;
}

export const NODE_WIDTH = 230;
export const NODE_HEIGHT = 104;

/** Left-to-right dagre layout; states without edges are placed too. */
export function computeLayout(wf: EditableWorkflow): Record<string, LayoutPosition> {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  // ranksep must fit an edge label (trigger name + confidence) between two node columns,
  // otherwise labels render underneath the next node.
  g.setGraph({ rankdir: 'LR', nodesep: 56, ranksep: 200, marginx: 24, marginy: 24 });
  Object.keys(wf.states).forEach((id) => g.setNode(id, { width: NODE_WIDTH, height: NODE_HEIGHT }));
  wf.edges.forEach((e) => {
    if (wf.states[e.source] && wf.states[e.target] && e.source !== e.target) g.setEdge(e.source, e.target);
  });
  dagre.layout(g);
  const out: Record<string, LayoutPosition> = {};
  Object.keys(wf.states).forEach((id) => {
    const n = g.node(id);
    out[id] = n
      ? { x: n.x - NODE_WIDTH / 2, y: n.y - NODE_HEIGHT / 2 }
      : { x: 0, y: 0 };
  });
  return out;
}

export function shortDigest(digest: string | undefined | null, length = 12): string {
  if (!digest) return '—';
  return digest.length > length ? `${digest.slice(0, length)}…` : digest;
}

export function formatTimestamp(value: string | undefined | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

// ─── Error extraction ───────────────────────────────────────────────────────

export interface WorkflowErrorInfo {
  status: number | null;
  message: string;
  /** Present on 409: the digest the server currently holds. */
  currentRevision?: string;
  /** Structured details (422 field problems, 502 upstream code, …). */
  details: string[];
}

function flattenDetail(node: unknown, path: string, out: string[], depth = 0): void {
  if (node == null || depth > 5) return;
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    out.push(path ? `${path}: ${String(node)}` : String(node));
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((item, i) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        const rec = item as Record<string, unknown>;
        const loc = Array.isArray(rec.loc)
          ? (rec.loc as unknown[]).join('.')
          : typeof rec.path === 'string'
            ? rec.path
            : typeof rec.field === 'string'
              ? rec.field
              : '';
        const msg =
          (typeof rec.msg === 'string' && rec.msg) ||
          (typeof rec.message === 'string' && rec.message) ||
          (typeof rec.problem === 'string' && rec.problem) ||
          (typeof rec.error === 'string' && rec.error) ||
          '';
        if (loc || msg) {
          out.push([path, loc].filter(Boolean).join(' ') + (msg ? `${loc || path ? ': ' : ''}${msg}` : ''));
          return;
        }
      }
      flattenDetail(item, path ? `${path}[${i}]` : `[${i}]`, out, depth + 1);
    });
    return;
  }
  if (typeof node === 'object') {
    Object.keys(node as Record<string, unknown>).forEach((key) => {
      flattenDetail((node as Record<string, unknown>)[key], path ? `${path}.${key}` : key, out, depth + 1);
    });
  }
}

/**
 * Turns an axios error from the workflow endpoints into something displayable.
 * Bodies seen on the stand: `{code, message}`, `{current_revision, …}`,
 * FastAPI-style `{detail: string | [{loc, msg}]}`, and free-form lists on 422.
 */
export function describeWorkflowError(err: unknown, fallback = 'Workflow request failed.'): WorkflowErrorInfo {
  const anyErr = err as any;
  const status: number | null = typeof anyErr?.response?.status === 'number' ? anyErr.response.status : null;
  const body = anyErr?.response?.data;
  const details: string[] = [];
  let message = '';
  let currentRevision: string | undefined;

  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.current_revision === 'string') currentRevision = b.current_revision;
    if (typeof b.message === 'string') message = b.message;
    else if (typeof b.detail === 'string') message = b.detail;
    else if (typeof b.error === 'string') message = b.error;
    else if (b.error && typeof b.error === 'object' && typeof (b.error as any).message === 'string') {
      message = (b.error as any).message;
    }

    const detailSources: Array<[string, unknown]> = [
      ['', b.detail],
      ['', b.errors],
      ['', b.problems],
      ['unresolved_refs', b.unresolved_refs],
      ['', b.entries],
      ['', b.items],
      ['upstream', b.upstream],
      ['upstream_code', b.upstream_code],
      ['upstream_status', b.upstream_status],
    ];
    detailSources.forEach(([label, value]) => {
      if (value == null || (typeof value === 'string' && value === message)) return;
      if (typeof value === 'string' || typeof value === 'number') {
        details.push(label ? `${label}: ${value}` : String(value));
        return;
      }
      flattenDetail(value, label, details);
    });
  } else if (typeof body === 'string' && body.trim()) {
    message = body.trim().slice(0, 400);
  }

  if (!message) {
    switch (status) {
      case 401:
        message = 'Session expired — sign in again.';
        break;
      case 403:
        message = 'Forbidden: this action requires the admin role and access to the integration.';
        break;
      case 404:
        message = 'The integration has no workflow surface (404).';
        break;
      case 409:
        message = 'The configuration changed since it was read. Reload and re-apply your edits.';
        break;
      case 422:
        message = 'The workflow was rejected by validation (422).';
        break;
      case 502:
        message = 'The upstream system failed to write the workflow (502).';
        break;
      default:
        message = typeof anyErr?.message === 'string' && anyErr.message ? anyErr.message : fallback;
    }
  }

  return { status, message, currentRevision, details: dedupe(details).slice(0, 50) };
}

function dedupe(list: string[]): string[] {
  const seen: Record<string, true> = {};
  return list.filter((item) => {
    if (seen[item]) return false;
    seen[item] = true;
    return true;
  });
}

export function downloadJson(filename: string, value: unknown): void {
  const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
